import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryKey } from '@/Config/api/queryKey';
import { AppActionDto } from '@/Domain/api/Action/dto/Action.dto';
import { CampaignDto } from '@/Domain/api/Action/dto/Campaign.dto';
import { ActionBox } from '@/Domain/components/Action/ActionBox/ActionBox';
import { CampaignSheet } from '@/Domain/components/Action/CampaignAction/Campaign.sheet';
import { AppActionTypeEnum } from '@/Domain/enum/ActionType.enum';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';

type Props = {
  campaign: CampaignDto;
};

export const CampaignBox = ({ campaign }: Props) => {
  const { t } = useTranslation('campaigns');
  const { toggleModal } = useModalStore();

  const { data } = useQuery<CampaignDto>({ queryKey: [QueryKey.campaign.get, campaign.id] });

  const mutation = useMutation<void, unknown, CampaignDto>({
    mutationKey: [QueryKey.campaign.update, campaign.id],
  });

  const action: AppActionDto = useMemo(
    () => ({
      id: (data ?? campaign).id,
      name: (data ?? campaign).name,
      description: (data ?? campaign).description,
      is_valid: true,
      is_active: (data ?? campaign).is_enabled,
      icon: 'FaAnchor',
      icon_color: '#5F58FE',
      type: AppActionTypeEnum.Campaign,
    }),
    [data, campaign],
  );

  const handleActive = (isActive: boolean) =>
    mutation.mutate({ ...(data ?? campaign), id: campaign.id, is_enabled: isActive });

  const handleEdit = () => toggleModal(MODAL_NAMES.CAMPAIGN_SHEET);

  return (
    <>
      <ActionBox
        dataTestId="campaign-action"
        action={action}
        onActive={handleActive}
        onEdit={handleEdit}
        buttonLabel={t('button.edit')}
      />
      <CampaignSheet campaign={data ?? campaign} />
    </>
  );
};
